import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  ArrowLeft, Loader2, ShieldAlert, Users, Mail, Building2, Check, X, Send,
  ChevronDown, UserCog, Inbox, AlertCircle, Search,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import {
  isPlatformAdmin, listAdminUsers, listPendingRequests,
  approveRequest, rejectRequest, assignMembership,
  type AdminUserRow, type PendingRequest,
} from "@/lib/permissions";
import { listAccessibleTenants, type AccessibleTenant } from "@/lib/admin-tenants";

export const Route = createFileRoute("/admin/users")({
  head: () => ({ meta: [{ title: "Admin · Usuarios — ZYNTRA" }] }),
  component: AdminUsersPage,
});

function AdminUsersPage() {
  const { user, loading: authLoading } = useAuth();
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [tab, setTab] = useState<"requests" | "users">("requests");
  const [users, setUsers] = useState<AdminUserRow[] | null>(null);
  const [pending, setPending] = useState<PendingRequest[] | null>(null);
  const [tenants, setTenants] = useState<AccessibleTenant[]>([]);
  const [search, setSearch] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setAllowed(false);
      return;
    }
    isPlatformAdmin(user.id).then(setAllowed);
  }, [user, authLoading]);

  async function loadAll() {
    try {
      const [u, p, t] = await Promise.all([
        listAdminUsers(),
        listPendingRequests(),
        listAccessibleTenants(),
      ]);
      setUsers(u);
      setPending(p);
      setTenants(t);
    } catch (e: any) {
      setError(e?.message ?? "Error al cargar datos.");
    }
  }

  useEffect(() => {
    if (allowed) loadAll();
  }, [allowed]);

  if (authLoading || allowed === null) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!allowed) {
    return (
      <div className="mx-auto max-w-md px-4 pt-32 text-center">
        <ShieldAlert className="mx-auto h-10 w-10 text-muted-foreground" />
        <h1 className="mt-3 text-xl font-bold text-foreground">Acceso restringido</h1>
        <p className="mt-1 text-sm text-muted-foreground">Solo platform admins pueden ver esta página.</p>
        <Link to="/dashboard" className="mt-4 inline-block text-sm text-primary hover:underline">
          Volver al dashboard
        </Link>
      </div>
    );
  }

  const filteredUsers = (users ?? []).filter((u) =>
    !search.trim()
      ? true
      : (u.email + " " + (u.display_name ?? "")).toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="mx-auto max-w-6xl px-4 pb-20 pt-24 sm:px-6">
      <Link
        to="/dashboard"
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Volver al dashboard
      </Link>

      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Usuarios y permisos</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Aprobá solicitudes y gestioná membresías a tenants.
          </p>
        </div>
      </div>

      {error && (
        <p className="mt-4 rounded-lg bg-destructive/10 px-3 py-2 text-xs text-destructive">{error}</p>
      )}

      <div className="mt-6 inline-flex items-center gap-1 rounded-lg bg-secondary p-1">
        <button
          onClick={() => setTab("requests")}
          className={`inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-xs font-bold transition-colors ${
            tab === "requests" ? "bg-background text-foreground" : "text-muted-foreground"
          }`}
        >
          <Inbox className="h-3.5 w-3.5" /> Solicitudes
          {pending && pending.length > 0 && (
            <span className="ml-1 rounded-full bg-primary px-1.5 py-0.5 text-[10px] text-primary-foreground">
              {pending.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setTab("users")}
          className={`inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-xs font-bold transition-colors ${
            tab === "users" ? "bg-background text-foreground" : "text-muted-foreground"
          }`}
        >
          <Users className="h-3.5 w-3.5" /> Usuarios ({users?.length ?? 0})
        </button>
      </div>

      {tab === "requests" ? (
        <RequestsTab pending={pending} tenants={tenants} reviewerId={user!.id} onChange={loadAll} />
      ) : (
        <UsersTab
          users={filteredUsers}
          tenants={tenants}
          search={search}
          setSearch={setSearch}
          onChange={loadAll}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Requests tab
// ─────────────────────────────────────────────────────────────────

function RequestsTab({
  pending, tenants, reviewerId, onChange,
}: {
  pending: PendingRequest[] | null;
  tenants: AccessibleTenant[];
  reviewerId: string;
  onChange: () => void;
}) {
  if (!pending) {
    return <div className="mt-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>;
  }
  if (pending.length === 0) {
    return (
      <div className="mt-8 rounded-2xl border border-dashed border-border bg-card/50 p-10 text-center">
        <Inbox className="mx-auto h-10 w-10 text-muted-foreground" />
        <p className="mt-3 text-sm text-muted-foreground">No hay solicitudes pendientes.</p>
      </div>
    );
  }
  return (
    <div className="mt-6 space-y-3">
      {pending.map((r) => (
        <RequestCard key={r.request_id} request={r} tenants={tenants} reviewerId={reviewerId} onChange={onChange} />
      ))}
    </div>
  );
}

function RequestCard({
  request, tenants, reviewerId, onChange,
}: {
  request: PendingRequest;
  tenants: AccessibleTenant[];
  reviewerId: string;
  onChange: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [tenantId, setTenantId] = useState<string>(tenants[0]?.id ?? "");
  const [role, setRole] = useState("member");
  const [canCreate, setCanCreate] = useState(true);
  const [canVenues, setCanVenues] = useState(false);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState<"approve" | "reject" | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function handleApprove() {
    setBusy("approve");
    setErr(null);
    try {
      await approveRequest(request.request_id, reviewerId, {
        tenantId: tenantId || undefined,
        role,
        canCreateEvents: canCreate,
        canManageVenues: canVenues,
        notes: notes.trim() || undefined,
      });
      onChange();
    } catch (e: any) {
      setErr(e?.message ?? "Error al aprobar.");
    } finally {
      setBusy(null);
    }
  }
  async function handleReject() {
    setBusy("reject");
    setErr(null);
    try {
      await rejectRequest(request.request_id, reviewerId, notes);
      onChange();
    } catch (e: any) {
      setErr(e?.message ?? "Error al rechazar.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-2 text-sm font-bold text-foreground">
            <Mail className="h-3.5 w-3.5 text-muted-foreground" />
            {request.email}
          </p>
          {request.display_name && (
            <p className="mt-0.5 text-xs text-muted-foreground">{request.display_name}</p>
          )}
          <p className="mt-1 text-[11px] text-muted-foreground">
            Solicitada {new Date(request.requested_at).toLocaleDateString("es-AR")}
          </p>
          {request.message && (
            <div className="mt-3 rounded-lg bg-secondary p-3">
              <p className="text-xs leading-relaxed text-foreground">{request.message}</p>
            </div>
          )}
        </div>
        <button
          onClick={() => setExpanded((v) => !v)}
          className="rounded-lg bg-secondary p-2 text-muted-foreground"
        >
          <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`} />
        </button>
      </div>

      {expanded && (
        <div className="mt-4 space-y-3 border-t border-border pt-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                Asignar a tenant
              </label>
              <select
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
                className="mt-1 w-full rounded-md border border-border bg-secondary px-3 py-1.5 text-xs text-foreground focus:border-primary focus:outline-none"
              >
                <option value="">— Sin tenant —</option>
                {tenants.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Rol</label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="mt-1 w-full rounded-md border border-border bg-secondary px-3 py-1.5 text-xs text-foreground focus:border-primary focus:outline-none"
              >
                <option value="member">Member</option>
                <option value="admin">Admin</option>
                <option value="owner">Owner</option>
              </select>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <label className="inline-flex items-center gap-2 text-xs text-foreground">
              <input
                type="checkbox"
                checked={canCreate}
                onChange={(e) => setCanCreate(e.target.checked)}
                className="h-3.5 w-3.5 accent-primary"
              />
              Puede crear eventos
            </label>
            <label className="inline-flex items-center gap-2 text-xs text-foreground">
              <input
                type="checkbox"
                checked={canVenues}
                onChange={(e) => setCanVenues(e.target.checked)}
                className="h-3.5 w-3.5 accent-primary"
              />
              Puede gestionar venues
            </label>
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Notas (opcional, visible al usuario si rechazás)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="mt-1 w-full rounded-md border border-border bg-secondary px-3 py-1.5 text-xs text-foreground focus:border-primary focus:outline-none"
            />
          </div>

          {err && <p className="text-xs text-destructive">{err}</p>}

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={handleApprove}
              disabled={busy !== null}
              className="inline-flex items-center gap-1.5 rounded-lg bg-success px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50"
            >
              {busy === "approve" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
              Aprobar
            </button>
            <button
              onClick={handleReject}
              disabled={busy !== null}
              className="inline-flex items-center gap-1.5 rounded-lg bg-destructive px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50"
            >
              {busy === "reject" ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
              Rechazar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Users tab
// ─────────────────────────────────────────────────────────────────

function UsersTab({
  users, tenants, search, setSearch, onChange,
}: {
  users: AdminUserRow[];
  tenants: AccessibleTenant[];
  search: string;
  setSearch: (s: string) => void;
  onChange: () => void;
}) {
  const [assignFor, setAssignFor] = useState<AdminUserRow | null>(null);

  return (
    <div className="mt-6">
      <div className="relative mb-4">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por email o nombre…"
          className="w-full rounded-lg border border-border bg-secondary py-2 pl-9 pr-3 text-sm text-foreground focus:border-primary focus:outline-none"
        />
      </div>

      {users.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-border bg-card/50 p-8 text-center text-sm text-muted-foreground">
          Sin resultados.
        </p>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-secondary text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-2.5 text-left">Email</th>
                <th className="px-4 py-2.5 text-left">Rol global</th>
                <th className="px-4 py-2.5 text-center">Tenants</th>
                <th className="px-4 py-2.5 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.user_id} className="border-t border-border">
                  <td className="px-4 py-3">
                    <p className="font-semibold text-foreground">{u.email}</p>
                    {u.display_name && (
                      <p className="text-xs text-muted-foreground">{u.display_name}</p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <RoleBadge role={u.global_role} />
                    {u.has_pending_request && (
                      <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-warning/15 px-2 py-0.5 text-[10px] font-bold text-warning">
                        <AlertCircle className="h-2.5 w-2.5" /> Solicitud
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-bold text-foreground">
                      {u.tenant_count}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => setAssignFor(u)}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground"
                    >
                      <UserCog className="h-3 w-3" /> Asignar tenant
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {assignFor && (
        <AssignMembershipModal
          user={assignFor}
          tenants={tenants}
          onClose={() => setAssignFor(null)}
          onSaved={() => {
            setAssignFor(null);
            onChange();
          }}
        />
      )}
    </div>
  );
}

function RoleBadge({ role }: { role: string }) {
  const isAdmin = role.includes("admin");
  const isOrg = role.includes("organizer");
  const cls = isAdmin
    ? "bg-primary/15 text-primary"
    : isOrg
      ? "bg-success/15 text-success"
      : "bg-muted text-muted-foreground";
  const label = isAdmin ? "Admin" : isOrg ? "Organizer" : "Viewer";
  return (
    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${cls}`}>{label}</span>
  );
}

function AssignMembershipModal({
  user, tenants, onClose, onSaved,
}: {
  user: AdminUserRow;
  tenants: AccessibleTenant[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [tenantId, setTenantId] = useState(tenants[0]?.id ?? "");
  const [role, setRole] = useState("member");
  const [canCreate, setCanCreate] = useState(true);
  const [canVenues, setCanVenues] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    if (!tenantId) {
      setErr("Seleccioná un tenant.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await assignMembership(user.user_id, tenantId, role, canCreate, canVenues);
      onSaved();
    } catch (e: any) {
      setErr(e?.message ?? "Error al guardar.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-background/60 backdrop-blur-sm sm:items-center">
      <div className="w-full max-w-md rounded-t-3xl border border-border bg-card p-5 sm:rounded-3xl">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-base font-bold text-foreground">Asignar a tenant</h3>
            <p className="text-xs text-muted-foreground">{user.email}</p>
          </div>
          <button onClick={onClose} className="rounded-lg bg-secondary p-2 text-muted-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-4 space-y-3">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Tenant</label>
            <select
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              className="mt-1 w-full rounded-md border border-border bg-secondary px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none"
            >
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Rol</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="mt-1 w-full rounded-md border border-border bg-secondary px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none"
            >
              <option value="member">Member</option>
              <option value="admin">Admin</option>
              <option value="owner">Owner</option>
            </select>
          </div>

          <div className="space-y-2 rounded-lg bg-secondary p-3">
            <label className="flex items-center gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                checked={canCreate}
                onChange={(e) => setCanCreate(e.target.checked)}
                className="h-4 w-4 accent-primary"
              />
              Puede crear eventos
            </label>
            <label className="flex items-center gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                checked={canVenues}
                onChange={(e) => setCanVenues(e.target.checked)}
                className="h-4 w-4 accent-primary"
              />
              Puede gestionar venues
            </label>
          </div>

          {err && <p className="text-xs text-destructive">{err}</p>}

          <button
            onClick={save}
            disabled={busy}
            className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg gradient-primary px-4 py-2.5 text-sm font-bold text-primary-foreground disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Building2 className="h-4 w-4" />}
            Guardar membresía
          </button>
        </div>
      </div>
    </div>
  );
}
